import { Control, useController, FieldPath } from 'react-hook-form';
import { AceGenericFieldLayout } from './AceGenericFieldLayout';
import { ReactNode } from 'react';
import { NumericInput } from '@blueprintjs/core';
import { defaultPhoneNumberFormatter } from './common';
import styles from './AcePhoneNumberField.module.css';

interface AcePhoneNumberFieldProps<T extends object, V, K> {
  control: Control<T>;
  phoneNumberName: FieldPath<T>;
  countryCodeName: FieldPath<T>;
  formatter?: (code: V, number: K) => ReactNode;
  editable?: boolean;
  label?: ReactNode;
  warning?: ReactNode;
  vertical?: boolean;
}

export function AcePhoneNumberField<T extends object, V, K>({
  control,
  phoneNumberName,
  countryCodeName,
  editable,
  label,
  formatter = defaultPhoneNumberFormatter,
  warning,
  vertical,
}: AcePhoneNumberFieldProps<T, V, K>) {
  const { field: codeField, fieldState: codeFieldState } = useController({
    control,
    name: countryCodeName,
  });
  const { field: phoneNumberField, fieldState: phoneNumberFieldState } =
    useController({ control, name: phoneNumberName });

  const formattedValue = formatter(codeField.value, phoneNumberField.value);

  return (
    <AceGenericFieldLayout
      editable={editable}
      label={label}
      error={
        codeFieldState.error?.message ?? phoneNumberFieldState.error?.message
      }
      warning={warning}
      vertical={vertical}
      valueElement={formattedValue}
      inputElement={
        <div className={styles.layout}>
          <strong>+</strong>
          <NumericInput
            className={styles.inputGroup}
            name={codeField.name}
            onValueChange={(valueAsNumber) => {
              codeField.onChange(valueAsNumber);

              // Prevents outdated validation errors
              phoneNumberField.onChange(phoneNumberField.value);
            }}
            onBlur={codeField.onBlur}
            value={codeField.value ?? ''}
            allowNumericCharactersOnly={true}
            inputRef={codeField.ref}
            fill
            buttonPosition="none"
          />
          <NumericInput
            className={styles.inputGroup}
            name={phoneNumberField.name}
            onValueChange={(_, valueAsString) => {
              phoneNumberField.onChange(valueAsString);

              // Prevents outdated validation errors
              codeField.onChange(codeField.value);
            }}
            onBlur={phoneNumberField.onBlur}
            value={phoneNumberField.value ?? ''}
            allowNumericCharactersOnly={true}
            inputRef={phoneNumberField.ref}
            fill
            buttonPosition="none"
          />
        </div>
      }
    />
  );
}
